import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import Icon from '../primitives/Icon';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/cn';
import './chat.css';

const ROW_HEIGHT = 30;        // px — keep in sync with .qds-row height below
const HEADER_HEIGHT = 48;   // fits the name line + dtype badge without clipping
const OVERSCAN = 8;           // rows rendered above/below the viewport
const PAGE_SIZE = 200;        // rows fetched per request
const ROW_INDEX_WIDTH = 56;
const COL_DEFAULT_WIDTH = 140;
const COL_MIN_WIDTH = 60;

/**
 * Cell coercion: numeric values get tabular alignment + monospaced look,
 * empty strings become a faint em-dash so the eye can scan blanks vs numbers.
 */
const looksNumeric = (s) => typeof s === 'string' && s !== '' && /^-?\d[\d.,_eE+-]*%?$/.test(s);

const dtypeLabel = (dtype) => {
  if (!dtype) return 'obj';
  const t = dtype.toLowerCase();
  if (t.startsWith('int') || t.startsWith('uint')) return 'int';
  if (t.startsWith('float')) return 'num';
  if (t.startsWith('bool')) return 'bool';
  if (t.includes('datetime') || t.includes('time')) return 'date';
  if (t.includes('category')) return 'cat';
  return 'str';
};

/** A single dataset's data + row-window cache, fetched lazily on scroll. */
function useWindowedDataset(datasetId) {
  const [columns, setColumns] = useState([]);     // [{name, dtype}]
  const [totalRows, setTotalRows] = useState(0);
  const [rows, setRows] = useState({});           // { rowIndex: [string, ...] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inflightRef = useRef(new Set());          // page-start indexes currently fetching

  // Reset on dataset change.
  useEffect(() => {
    setColumns([]); setTotalRows(0); setRows({}); setError(null);
    inflightRef.current = new Set();
    if (!datasetId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await apiService.getDatasetWindow(datasetId, { offset: 0, limit: PAGE_SIZE });
        if (cancelled) return;
        const meta = resp.data.column_meta || resp.data.preview.columns.map(c => ({ name: c, dtype: 'object' }));
        const fetched = resp.data.preview.rows || [];
        const total = resp.data.total_rows ?? fetched.length;
        setColumns(meta);
        setTotalRows(total);
        const next = {};
        fetched.forEach((r, i) => { next[i] = r; });
        setRows(next);
      } catch (e) {
        if (!cancelled) setError('Failed to load dataset.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [datasetId]);

  const ensureRange = useCallback((startRow, endRow) => {
    if (!datasetId || !totalRows) return;
    // Snap to page boundaries.
    const firstPage = Math.floor(startRow / PAGE_SIZE) * PAGE_SIZE;
    const lastPage = Math.floor(endRow / PAGE_SIZE) * PAGE_SIZE;
    for (let p = firstPage; p <= lastPage; p += PAGE_SIZE) {
      if (p >= totalRows) break;
      if (rows[p] !== undefined) continue;             // already loaded (header row of page)
      if (inflightRef.current.has(p)) continue;
      inflightRef.current.add(p);
      apiService.getDatasetWindow(datasetId, { offset: p, limit: PAGE_SIZE })
        .then(resp => {
          const fetched = resp.data.preview.rows || [];
          setRows(prev => {
            const next = { ...prev };
            fetched.forEach((r, i) => { next[p + i] = r; });
            return next;
          });
        })
        .catch(() => { /* leave the page unfetched; next scroll retries */ })
        .finally(() => { inflightRef.current.delete(p); });
    }
  }, [datasetId, totalRows, rows]);

  return { columns, totalRows, rows, loading, error, ensureRange };
}

const Cell = ({ value, isNumeric, width }) => (
  <div
    title={value}
    className={cn(
      'q-grid__cell',
      isNumeric && 'q-grid__cell--num',
      value === '' && 'q-grid__cell--empty',
    )}
    style={{
      width, minWidth: width, maxWidth: width,
      height: ROW_HEIGHT,
      lineHeight: `${ROW_HEIGHT}px`,
    }}
  >
    {value === '' ? '—' : value}
  </div>
);

/** Spreadsheet body: virtualized row windowing without external deps. */
const Grid = ({ columns, totalRows, rows, ensureRange, columnWidths }) => {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(400);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setViewportH(el.clientHeight));
    ro.observe(el);
    setViewportH(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  const onScroll = (e) => setScrollTop(e.currentTarget.scrollTop);

  const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(viewportH / ROW_HEIGHT) + OVERSCAN * 2;
  const endRow = Math.min(totalRows, startRow + visibleCount);

  useEffect(() => {
    if (totalRows > 0) ensureRange(startRow, endRow);
  }, [startRow, endRow, totalRows, ensureRange]);

  const totalHeight = totalRows * ROW_HEIGHT;
  const offsetY = startRow * ROW_HEIGHT;

  const totalGridWidth = ROW_INDEX_WIDTH + columnWidths.reduce((a, b) => a + b, 0);

  const headerRow = (
    <div className="q-grid__head" style={{ height: HEADER_HEIGHT, width: totalGridWidth }}>
      <div className="q-grid__index" style={{ width: ROW_INDEX_WIDTH, minWidth: ROW_INDEX_WIDTH, justifyContent: 'center' }}>#</div>
      {columns.map((col, i) => (
        <div
          key={col.name + i}
          className="q-grid__col"
          style={{ width: columnWidths[i], minWidth: columnWidths[i], maxWidth: columnWidths[i] }}
        >
          <div className="q-grid__col-name" title={col.name}>{col.name}</div>
          <div className="q-grid__dtype">{dtypeLabel(col.dtype)}</div>
        </div>
      ))}
    </div>
  );

  const visibleRows = [];
  for (let r = startRow; r < endRow; r += 1) {
    const rowData = rows[r];
    visibleRows.push(
      <div
        key={r}
        className={cn('q-grid__row', r % 2 === 1 && 'q-grid__row--alt')}
        style={{ height: ROW_HEIGHT, width: totalGridWidth }}
      >
        <div
          className="q-grid__index"
          style={{ width: ROW_INDEX_WIDTH, minWidth: ROW_INDEX_WIDTH, height: ROW_HEIGHT }}
        >{(r + 1).toLocaleString()}</div>
        {columns.map((col, i) => {
          const value = rowData ? (rowData[i] ?? '') : '';
          const numeric = looksNumeric(value);
          return (
            <Cell
              key={col.name + i}
              value={rowData ? value : '·'}
              isNumeric={numeric}
              width={columnWidths[i]}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="q-grid" onScroll={onScroll}>
      <div style={{ width: totalGridWidth, position: 'relative' }}>
        {headerRow}
        <div style={{ height: totalHeight, position: 'relative' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            transform: `translateY(${offsetY}px)`, willChange: 'transform',
          }}>
            {visibleRows}
          </div>
        </div>
      </div>
    </div>
  );
};

const SegmentedToggle = ({ options, value, onChange }) => (
  <div className="q-seg">
    {options.map(opt => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          className={cn('q-seg__btn', active && 'q-seg__btn--active')}
          onClick={() => onChange(opt.value)}
          disabled={opt.disabled}
          title={opt.title || ''}
        >
          {opt.icon && <Icon name={opt.icon} size={12} />}
          <span>{opt.label}</span>
          {opt.count != null && <span className="q-seg__count">{opt.count}</span>}
        </button>
      );
    })}
  </div>
);

/**
 * Modal spreadsheet viewer with a Raw / Preprocessed pivot.
 *
 * Props:
 *   preprocessed: { id, name, rows_count?, columns_count? } | null
 *   rawUploads:   [{ id, name, original_filename?, ...}]
 *   onClose:      () => void
 *   initialView:  'raw' | 'preprocessed'
 */
const DatasetViewer = ({ preprocessed, rawUploads = [], onClose, initialView }) => {
  const hasPre = !!preprocessed;
  const hasRaw = rawUploads.length > 0;
  const defaultView = initialView || (hasPre ? 'preprocessed' : 'raw');
  const [view, setView] = useState(defaultView);
  const [rawIdx, setRawIdx] = useState(0);

  // If the active view becomes invalid (e.g., raw with no uploads), fall back.
  useEffect(() => {
    if (view === 'raw' && !hasRaw) setView(hasPre ? 'preprocessed' : 'raw');
    if (view === 'preprocessed' && !hasPre) setView(hasRaw ? 'raw' : 'preprocessed');
  }, [view, hasPre, hasRaw]);

  const activeDataset = view === 'preprocessed' ? preprocessed : rawUploads[rawIdx];
  const { columns, totalRows, rows, loading, error, ensureRange } = useWindowedDataset(activeDataset?.id);

  // Compute column widths once columns load. Sample the first ~20 cached rows
  // to size each column to its content (capped) so the grid feels tactile.
  const columnWidths = useMemo(() => {
    if (!columns.length) return [];
    const sampleRows = Object.values(rows).slice(0, 30);
    return columns.map((col, i) => {
      let widest = String(col.name).length;
      for (const r of sampleRows) {
        const v = r[i];
        if (v && v.length > widest) widest = v.length;
      }
      // ~7px per character, plus padding; clamp.
      return Math.max(COL_MIN_WIDTH, Math.min(360, Math.round(widest * 7.2) + 22)) || COL_DEFAULT_WIDTH;
    });
  }, [columns, rows]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = activeDataset?.name || activeDataset?.original_filename || 'Dataset';
  const subtitle = (() => {
    const r = totalRows;
    const c = columns.length;
    if (!r) return loading ? 'Loading…' : (error || '');
    return `${r.toLocaleString()} rows · ${c} columns`;
  })();

  return (
    <div
      className="q-viewer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div className="q-viewer__panel">
        <div className="q-viewer__head">
          <div className="q-rail__icon">
            <Icon name="file-spreadsheet" size={15} />
          </div>
          <div className="q-viewer__name">
            <div className="q-viewer__title">{title}</div>
            <div className="q-viewer__sub">{subtitle}</div>
          </div>

          <SegmentedToggle
            value={view}
            onChange={setView}
            options={[
              {
                value: 'raw',
                label: 'Raw',
                icon: 'file-text',
                count: rawUploads.length || null,
                disabled: !hasRaw,
                title: hasRaw ? 'Original uploaded files' : 'No raw uploads available',
              },
              {
                value: 'preprocessed',
                label: 'Preprocessed',
                icon: 'sparkles',
                disabled: !hasPre,
                title: hasPre ? 'After cleaning + merge pipeline' : 'No preprocessed dataset yet',
              },
            ]}
          />

          <Button variant="ghost" size="icon" onClick={onClose} title="Close" aria-label="Close">
            <Icon name="x" data-icon="inline-start" />
          </Button>
        </div>

        {view === 'raw' && rawUploads.length > 1 && (
          <div className="q-viewer__files">
            <span className="q-viewer__files-label">File</span>
            {rawUploads.map((u, i) => {
              const active = i === rawIdx;
              const label = u.original_filename || u.name || `File ${i + 1}`;
              return (
                <button
                  key={u.id}
                  type="button"
                  className={cn('q-filechip', active && 'q-filechip--active')}
                  onClick={() => setRawIdx(i)}
                  title={label}
                >{label}</button>
              );
            })}
          </div>
        )}

        {error ? (
          <div className="q-viewer__empty">{error}</div>
        ) : loading && !columns.length ? (
          <div className="q-viewer__status">
            <div className="q-spin" />
            Loading dataset…
          </div>
        ) : !columns.length ? (
          <div className="q-viewer__empty">No data available.</div>
        ) : (
          <Grid
            columns={columns}
            totalRows={totalRows}
            rows={rows}
            ensureRange={ensureRange}
            columnWidths={columnWidths}
          />
        )}

        <div className="q-viewer__foot">
          <Icon name={view === 'preprocessed' ? 'sparkles' : 'file-text'} size={12} />
          {view === 'preprocessed'
            ? 'Pipeline output: merged, type-coerced, leakage-pruned. Use this for modeling.'
            : 'Raw upload as ingested. Compare to the preprocessed view to see what changed.'}
        </div>
      </div>
    </div>
  );
};

export default DatasetViewer;
